import React, { useEffect, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { getTeamsEnhancedCti } from '../api';
import { dialConferencePhone, holdFor, intrude, monitor, pickup } from '../api/call-control';
import { useSelector } from '../hook-utils';
import { DeviceGroupType, EntityType, Extension, ExtensionNumbers, ExtensionStatus, getKey, Presence, UserLevel } from '../model';
import { actions } from '../store';
import { selectActiveDevice } from '../store/reducers';
import { selectApplicationSetting, selectConfigProp, selectFavExtensionFolderId, selectMyAccess, selectMyself, selectVoicemailPorts } from '../store/reducers/config';
import { selectInIpfxClient } from '../store/reducers/localSettings';
import { selectMyActiveCall } from '../store/reducers/media';
import { MonitorDesktopState, selectDesktopMonitorExtension, selectExtension, selectExtensionDetails, selectFavExtensions } from '../store/reducers/queueAgents';
import { getJitsi } from '../store/reducers/settings';
import { isTruthy } from '../type-utils';
import { ReactComponent as AboutIcon } from './icons/about.svg';
import { ReactComponent as AddIcon } from './icons/add.svg';
import { ReactComponent as ConferenceIcon } from './icons/callcontrol/conference.svg';
import { ReactComponent as HoldForIcon } from './icons/callcontrol/hold-for.svg';
import { ReactComponent as IntrudeIcon } from './icons/callcontrol/intrude.svg';
import { ReactComponent as MonitorIcon } from './icons/callcontrol/monitor.svg';
import { ReactComponent as PickUpIcon } from './icons/callcontrol/pickup.svg';
import { ReactComponent as SetCallBackIcon } from './icons/callcontrol/set-callback.svg';
import { ReactComponent as DialAssociateIcon } from './icons/dial-associate.svg';
import { ReactComponent as DialIcon } from './icons/dial-desk.svg';
import { ReactComponent as DialHomeIcon } from './icons/dial-home.svg';
import { ReactComponent as DialMobileIcon } from './icons/dial-mobile.svg';
import { ReactComponent as DialOtherIcon } from './icons/dial-other.svg';
import { ReactComponent as EmailIcon } from './icons/email.svg';
import { ReactComponent as ExtesnionPropsIcon } from './icons/extension-props.svg';
import { ReactComponent as FavRemoveIcon } from './icons/favourite-remove.svg';
import { ReactComponent as FavIcon } from './icons/favourite.svg';
import { ReactComponent as InstantMessagerIcon } from './icons/instantmessager/chat.svg';
import { ReactComponent as MonitorDeskIcon } from './icons/mointor.svg';
import { ReactComponent as SettingsIcon } from './icons/settings.svg';
import { ReactComponent as StatsIcon } from './icons/stats.svg';
import { ReactComponent as VoicemailIcon } from './icons/voicemail.svg';
import { Menu, MenuDivider, MenuItem, MenuTarget, SubMenu } from './Menu';
import PresenceIcon from './PresenceIcon';
import Toggle from './Toggle';

interface ExtensionMenuProps {
  extension: Extension
  target: MenuTarget
  onClose(): void
  enabledIm?: boolean
  onClick?(): void
}

interface ExtensionMenuItemsProps {
  extension: Extension
  children: (items: React.ReactNode) => React.ReactElement
  enabledIm?: boolean
  onClick?(): void
}

export default function ExtensionMenu({ extension, target, onClose, enabledIm, onClick }: ExtensionMenuProps) {
  return <ExtensionMenuItems extension={extension} enabledIm={enabledIm} >
    {children => <Menu className='AgentMenu' open={Boolean(extension)} target={target || null} onClose={onClose} onClick={onClick}>{children}</Menu>}
  </ExtensionMenuItems>
}

export function ExtensionMenuItems({ extension, children, enabledIm }: ExtensionMenuItemsProps) {
  const access = useSelector(selectMyAccess)
  const myTeamsEnhancedCti = useSelector(s => selectConfigProp(s, 'teamsEnhancedCti'))
  const device = useSelector(s => selectActiveDevice(s)?.number)
  const call = useSelector(selectMyActiveCall)
  const vmPorts = useSelector(selectVoicemailPorts)
  const dispatch = useDispatch()
  const favExtensions = useSelector(selectFavExtensions)
  const folderId = useSelector(selectFavExtensionFolderId)
  const extensionDetails = useSelector(s => selectExtensionDetails(s, { id: extension.id, server: extension.server }))
  const auto = useSelector(s => selectApplicationSetting(s, 'ExtensionAutoAnswer'))
  const jitsi = useSelector(getJitsi)
  const showTeamsEnhanced = useSelector(s => selectConfigProp(s, 'teamsEnhanced'))
  const userLevel = useSelector(s => selectConfigProp(s, 'baseUserLevel'))
  const monitorDesktop = useSelector(s => selectDesktopMonitorExtension(s, { id: extension.id, server: extension.server }))

  const inIpfxClient = useSelector(selectInIpfxClient)
  const [otherTeamsEnhancedCti, setOtherTeamsEnhancedCti] = useState<boolean>()

  const myId = useSelector(selectMyself)
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)

  useEffect((() => {
    if (favExtensions) return
    if (!folderId) return

    dispatch(actions.fetchExtensionsRequeust(folderId))
  }), [dispatch, folderId, favExtensions])

  useEffect((() => {
    if (extensionDetails) return

    dispatch(actions.fetchExtensionDetails.request({ id: extension.id, server: extension.server }))
  }), [dispatch, folderId, favExtensions, extension, extensionDetails])

  useEffect((() => {
    let mounted = true

    if (!myId || extension.server !== myId.server) return () => { mounted = true }
    if (!myId || extension.id === myId.id) return () => { mounted = true }
    if (inIpfxClient && access.isTeamsPbx) return () => { mounted = true }

    load()
    return () => { mounted = false }

    async function load() {
      const result = await getTeamsEnhancedCti(extension.id)
      if (!mounted) { return }
      setOtherTeamsEnhancedCti(result)
    }

  }), [access.isTeamsPbx, inIpfxClient, extension.id, extension.server, myId])

  if (!extension || !myself || !device || !myId || !favExtensions || !extensionDetails || auto === undefined || auto === null) { return null }

  function onFav(add: boolean) {
    if (extension) {
      dispatch(actions.favouriteExtension(extension, add))
    }
  }

  const answered = myself.status === ExtensionStatus.Answered
  const released = myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released
  const isVmPortCall = call && vmPorts.find(x => x === call.clid) !== null
  const dialShow = access.features.phoneDial
  const transferShow = access.features.phoneXferBlind

  //teams deliever options
  const teamsEnhancedCti = myId.id === extension.id && myTeamsEnhancedCti !== undefined ? myTeamsEnhancedCti : myId.id !== extension.id && otherTeamsEnhancedCti !== undefined ? otherTeamsEnhancedCti : false
  const teamsPbxDeliverOptions = access.isTeamsPbx && extension.server === myId.server
  const teamsPbxDeliverOptionsDevice = teamsPbxDeliverOptions && ((access.features.extensionDeviceGroupsEditOwn && myId.id === extension.id) || (access.features.extensionDeviceGroupsEditOthers && myId.id !== extension.id)) && <MenuItem key='call-deliever-device'>
    <Toggle value={extension.deviceGroupType === DeviceGroupType.Other} onChange={c => dispatch(actions.teamsToggleDeviceGroup(c, { id: extension.id, server: extension.server }))}>
      Via Teams
</Toggle>
  </MenuItem>
  const teamsPbxDeliverOptionsEnhanced = teamsPbxDeliverOptions && (inIpfxClient && access.isTeamsPbx) && extension.id === myId.id && showTeamsEnhanced && <MenuItem key='call-deliever-intergration'>
    <Toggle value={teamsEnhancedCti} onChange={c => dispatch(actions.updateEnhancedIntegration(extension.id, c))}>
      Enhanced Integration
</Toggle>
  </MenuItem>
  const teamsPbxDelieverOptionsChildren = <SubMenu> {teamsPbxDeliverOptionsDevice}{teamsPbxDeliverOptionsEnhanced}</SubMenu>


  const extraItems = access.isLyncLicensed ? [] : [
    access.features.phoneHoldFor &&
    myself.status === ExtensionStatus.Answered &&
    extension.status !== ExtensionStatus.None && extension.status !== ExtensionStatus.Released &&
    <MenuItem key='holdFor' disabled={!isVmPortCall} onClick={() => call && holdFor(device, extension.id, call.id)}>
      <HoldForIcon />
      Park for
    </MenuItem>,

    access.features.phonePickup && !isVmPortCall &&
    (myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released) &&
    extension.status === ExtensionStatus.Ringing &&
    <MenuItem key='pickUp' onClick={() => pickup(myId.id, extension.id, auto.value === 'true')}>
      <PickUpIcon />
      Pick up
    </MenuItem>,

    access.features.phoneBusySetCallback && !isVmPortCall &&
    (
      (extension.status !== ExtensionStatus.None && extension.status !== ExtensionStatus.Released)
      ||
      (extension.presence !== Presence.Available && extension.presence !== Presence.Queue)
    ) &&
    <MenuItem key='setCallBack' onClick={() => dispatch(actions.setCallback(extension.id))}>
      <SetCallBackIcon />
      Set callback
    </MenuItem>,

    access.features.phoneMonitor &&
    (myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released) &&
    extension.status === ExtensionStatus.Answered && extensionDetails.monitor &&
    <MenuItem key='monitor' onClick={() => monitor(myself.id, extension.id)}>
      <MonitorIcon />
      Monitor
    </MenuItem>,

    access.features.phoneIntrude &&
    (myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released) &&
    extension.status === ExtensionStatus.Answered &&
    <MenuItem key='intrude' onClick={() => intrude(myself.id, extension.id)}>
      <IntrudeIcon />
      Intrude
    </MenuItem>,
  ].filter(isTruthy)

  return children(<>
    {((access.features.extensionMyLocation && myId.id === extension.id) || (access.features.extensionAllLocations && myId.id !== extension.id)) && !access.isTeamsPbx && <MenuItem key='set-presence' onClick={() => dispatch(actions.openSetPresence(extension))}>
      <PresenceIcon presence={Presence.Available} />
      Set Presence
    </MenuItem>}
    {isBrowser &&
      teamsPbxDeliverOptions && (teamsPbxDeliverOptionsDevice || teamsPbxDeliverOptionsEnhanced) && <MenuItem key='call-deliever' subMenu={teamsPbxDelieverOptionsChildren}>
        <SettingsIcon /> Deliver Options
    </MenuItem>}
    {
      access.features.clientViewExtensionDetails && <MenuItem key='view-details' onClick={() => {
        //if (onDetails) {
        //  onDetails(extension)
        //} else {
        dispatch(actions.setSelectedExtension(extension, false))
        //}
      }}><AboutIcon />View Details</MenuItem>
    }

    {isBrowser &&
      (access.features.extensionViewOtherVoicemail && myId.server === extension.server && extensionDetails.canReceiveVoicemail) && <MenuItem key='view-voicemail' onClick={() => dispatch(actions.openVoiceMail(extension!))}>
        <VoicemailIcon />
        View voicemail
    </MenuItem>
    }
    <MenuDivider />
    {renderDialMenuItem('officeNumber', undefined, <DialIcon />, 'Desk/Business', (answered ? transferShow : dialShow))}
    {renderDialMenuItem('associateNumber', undefined, <DialAssociateIcon />, 'Associate', (answered ? transferShow : dialShow))}
    {renderDialMenuItem('mobileNumber', 'isMobileNumberVisible', <DialMobileIcon />, 'Mobile', (answered ? transferShow : dialShow))}
    {renderDialMenuItem('homeNumber', 'isHomeNumberVisible', <DialHomeIcon />, 'Home', (answered ? transferShow : dialShow))}
    {renderDialMenuItem('otherNumber', 'isOtherNumberVisible', <DialOtherIcon />, 'Other', (answered ? transferShow : dialShow))}
    {isBrowser &&
      (access.features.phoneConference && !access.isLyncLicensed) && ((myself.status === ExtensionStatus.Hold || isVmPortCall) || !(myself.status === ExtensionStatus.Answered || (extension.status === ExtensionStatus.None || extension.status === ExtensionStatus.Released))) && <MenuItem key='create-conference' itemClass='large-svg' onClick={() => {
        dialConferencePhone(device, extension.id)
      }}>
        <AddIcon />
        Conference
    </MenuItem>
    }
    {isBrowser &&
      (access.isTeamsPbx === false && access.features.toolBarCollaboration && (myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released) && jitsi.id === '') && myself.id !== extension.id && <MenuItem key='create-jitsi-conference' disabled={jitsi.id !== ''} itemClass='large-svg' onClick={() => {
        dispatch(actions.ccCreateVideoConferenceFromExtension({ id: extension.id, server: extension.server }))
      }}>
        <ConferenceIcon />
        Video Call
    </MenuItem>
    }
    {renderDialMenuItem('email', undefined, <EmailIcon />, 'Email', true)}

    {isBrowser &&
      (access.features.extensionPSChat || access.features.extensionPSChatExternal) && <MenuItem key='im' onClick={() => dispatch(actions.requestOneOnOneConversation({ id: extension.id, server: extension.server }, EntityType.Extension))} disabled={myId.id === extension.id && myId.server === extension.server || enabledIm === false}>
        <InstantMessagerIcon />
        Send an Instant Message...
      </MenuItem>
    }

    {isBrowser && inIpfxClient && monitorDesktop !== MonitorDesktopState.disabled &&
      access.features.monitorAgentDesktop && myId.id !== extension.id && <MenuItem key='monitor-desk' itemClass="large-svg" onClick={() => dispatch(actions.ccRequestMonitorDesktop({ id: extension.id, server: extension.server }))} disabled={myId.id === extension.id && myId.server === extension.server || enabledIm === false}>
        <MonitorDeskIcon />
        {monitorDesktop === MonitorDesktopState.running ? 'Stop' : 'Start'} Monitor Desktop
      </MenuItem>
    }

    {extraItems.length > 0 && <MenuDivider />}
    {extraItems}

    {access.features.toolbarDirectory && <MenuDivider />}
    {access.features.toolbarDirectory && (!favExtensions.includes(getKey(extension)) ?
      <MenuItem key='add-fav' onClick={() => onFav(true)}>
        <FavIcon />
        Add to favourites
      </MenuItem>
      :
      <MenuItem key='remove-fav' onClick={() => onFav(false)}>
        <FavRemoveIcon />
        Remove from favourites
      </MenuItem>)
    }

    {isBrowser && <MenuDivider />}
    {isBrowser && userLevel !== undefined && userLevel >= UserLevel.Supervisor && userLevel <= UserLevel.IPFXTechnician && <MenuItem onClick={() => dispatch(actions.openAgentStats(extension.id))}>
      <StatsIcon />
      Statistics
      </MenuItem>
    }

    {isBrowser &&
      ((myId.id === extension.id && access.features.configMyExtension) || access.features.configAllExtensions) && <>

        <MenuDivider />
        <MenuItem key='properties' onClick={() => {
          dispatch(actions.setSelectedExtension(extension, true))
        }}>
          <ExtesnionPropsIcon />
          Properties
      </MenuItem>
      </>
    }

  </>)

  function renderDialMenuItem(key1: keyof ExtensionNumbers, key2: keyof ExtensionNumbers | undefined, icon: React.ReactNode, title: string, access: Boolean | undefined) {
    if (!extensionDetails) { return }
    const number = extensionDetails[key1]

    if (typeof number === 'boolean') return

    const handleClick =
      key1 === 'email' ? (email: string) => dispatch(actions.ccMailto(email)):
        answered ? (number: string) => dispatch(actions.ccBlindTransfer(number)) :
          (number: string) => dispatch(actions.ccDial(number))

    const prefix =
      key1 === 'email' ? '' :
        answered ? 'Transfer to ' :
          'Dial '

    const disabled = false
/*      02/12/2024 AR: UC-11446: Dial/transfer options are always valid, even on an answered call 
         key1 === 'email' ? false :
        answered ? !answered || (extension.status !== ExtensionStatus.None && extension.status !== ExtensionStatus.Released) :
          !released || (extension.status !== ExtensionStatus.None && extension.status !== ExtensionStatus.Released)
 */
    const visible = key2 === undefined ? true : extensionDetails[key2]

    return access && <MenuItem key={title} disabled={!number || !visible || disabled} onClick={(e) => {
      e.preventDefault()
      handleClick(number!)
    }}>
      {icon}
      {prefix + title}
    </MenuItem>
  }
}
